// Corpus analytics: pure aggregation over a set of parsed iReal charts (the
// bundled Jazz 1460 and, later, any loaded playlist). Chart-era analyses only —
// everything here reads harmony and form; melody-era analyses arrive with the
// Score corpus. No React, no fetching: callers parse the playlist and hand the
// charts in.
//
// Conventions:
// - Transitions follow PERFORMANCE order (chartToSequence flattens repeats/
//   endings/DC-DS), because "what follows what" is a statement about the tune
//   as played, not as typeset. Form LENGTH is reported as written.
// - Degree analysis is key-relative (semitones above the tonic as parsed from
//   the chart's key field), labelled with jazz-convention Roman degrees, and
//   qualities collapse to five families (∆ m 7 ø °) so vocabularies stay
//   readable: "IIm7 → V7" not "min9 rooted 2 semitones up…".

import { chartToSequence } from './chart';
import { pitchClassOf } from './notes';
import type { ChordTypeId } from './chords';
import type { IRealChart } from './ireal/types';
import type { SequenceChord } from './song';

// ---------------------------------------------------------------------------
// Vocabulary helpers
// ---------------------------------------------------------------------------

/** Collapse the 25 four-part types into five readable quality families. */
export function qualityFamily(type: ChordTypeId): '∆' | 'm' | '7' | 'ø' | '°' {
  if (type.startsWith('min7b5')) return 'ø';
  if (type.startsWith('dim')) return '°';
  if (type.startsWith('maj')) return '∆';
  if (type.startsWith('dom')) return '7';
  // The triad tier: 'maj'/'min'/'dim' land on the prefixes above, but an
  // augmented triad is a major-family chord and would otherwise read minor.
  if (type === 'aug') return '∆';
  return 'm';
}

const DEGREE_LABELS = ['I', '♭II', 'II', '♭III', 'III', 'IV', '♭V', 'V', '♭VI', 'VI', '♭VII', 'VII'];

/** Jazz-convention Roman degree for a root, relative to the tonic pitch class. */
export function degreeLabel(rootPc: number, tonicPc: number): string {
  return DEGREE_LABELS[(((rootPc - tonicPc) % 12) + 12) % 12];
}

/** "IIm7", "V7", "I∆" — the unit of the corpus's progression vocabulary. */
export function romanChord(chord: SequenceChord, tonicPc: number): string {
  return `${degreeLabel(pitchClassOf(chord.root), tonicPc)}${qualityFamily(chord.chordType)}`;
}

export interface ParsedKey {
  tonicPc: number;
  minor: boolean;
  /** Normalised display label, e.g. "E♭" or "G minor". */
  label: string;
}

/** Parse the chart's key field ("Eb", "G minor", "F#"). Unknown → C major. */
export function parseKeyLabel(key: string | undefined): ParsedKey {
  const trimmed = (key ?? '').trim();
  const minor = /minor$/i.test(trimmed);
  const root = trimmed.replace(/\s*minor$/i, '').trim() || 'C';
  let tonicPc = 0;
  try {
    tonicPc = pitchClassOf(root);
  } catch {
    tonicPc = 0;
  }
  const pretty = root.replace('b', '♭').replace('#', '♯');
  return { tonicPc, minor, label: minor ? `${pretty} minor` : pretty };
}

/** Musical label for a root motion of n semitones up (mod 12). */
export const ROOT_MOTION_LABELS: Record<number, string> = {
  0: 'same root',
  1: 'up ½ step',
  2: 'up a step',
  3: 'up min 3rd',
  4: 'up maj 3rd',
  5: 'down a 5th',
  6: 'tritone',
  7: 'up a 5th',
  8: 'down maj 3rd',
  9: 'down min 3rd',
  10: 'down a step',
  11: 'down ½ step',
};

// ---------------------------------------------------------------------------
// Per-tune facts
// ---------------------------------------------------------------------------

export interface TuneFacts {
  title: string;
  composer: string;
  style: string;
  key: ParsedKey;
  timeSig: string;
  /** Form length as written (authored bars). */
  bars: number;
  /** Form length as played (repeats expanded) — what the masthead counts. */
  playedBars: number;
  /** Chord events in performance order (repeats expanded). */
  chords: number;
  /** Chord changes per played bar — the tune's harmonic rhythm. */
  chordsPerBar: number;
  /** Distinct four-part chord types used. */
  distinctTypes: number;
  /** Roman-quality tokens in performance order (the tune's progression text). */
  romans: string[];
  /** Fraction of transitions that sit inside a ii→V (0..1). */
  iiVDensity: number;
  /** Chord sequence retained for downstream (e.g. voice-leading) analyses. */
  sequence: SequenceChord[];
}

/** ii→V transition: minor-family down a fifth to dominant-family. */
function isIiV(a: SequenceChord, b: SequenceChord): boolean {
  const up = (((pitchClassOf(b.root) - pitchClassOf(a.root)) % 12) + 12) % 12;
  return up === 5 && qualityFamily(a.chordType) === 'm' && qualityFamily(b.chordType) === '7';
}

/** ii→V→I resolution: an ii→V whose V falls a fifth onto a ∆ or m tonic. */
function isIiVI(a: SequenceChord, b: SequenceChord, c: SequenceChord): boolean {
  if (!isIiV(a, b)) return false;
  const up = (((pitchClassOf(c.root) - pitchClassOf(b.root)) % 12) + 12) % 12;
  const family = qualityFamily(c.chordType);
  return up === 5 && (family === '∆' || family === 'm');
}

export function tuneFacts(chart: IRealChart): TuneFacts {
  const key = parseKeyLabel(chart.key);
  // The header time signature defaults to 4/4; the real one rides the first
  // measure's T-token (a waltz chart says T34 in the music string).
  const timeSig = chart.measures.find((m) => m.timeSig)?.timeSig ?? chart.timeSignature;
  const sequence = chartToSequence(chart, { stringSet: 'middle' });
  const playedBars = sequence.length ? sequence[sequence.length - 1].barIndex + 1 : 0;
  const romans = sequence.map((c) => romanChord(c, key.tonicPc));
  let iiV = 0;
  for (let i = 1; i < sequence.length; i++) {
    if (isIiV(sequence[i - 1], sequence[i])) iiV++;
  }
  return {
    title: chart.title,
    composer: chart.composer ?? '',
    style: chart.style ?? '',
    key,
    timeSig: `${timeSig[0]}/${timeSig[1]}`,
    bars: chart.measures.length,
    playedBars,
    chords: sequence.length,
    chordsPerBar: playedBars > 0 ? sequence.length / playedBars : 0,
    distinctTypes: new Set(sequence.map((c) => c.chordType)).size,
    romans,
    iiVDensity: sequence.length > 1 ? iiV / (sequence.length - 1) : 0,
    sequence,
  };
}

// ---------------------------------------------------------------------------
// Corpus aggregation
// ---------------------------------------------------------------------------

export interface RankedCount {
  label: string;
  count: number;
}

export interface LeagueRow {
  title: string;
  composer: string;
  value: number;
}

export interface CorpusStats {
  tunes: number;
  composers: number;
  totalChords: number;
  totalBars: number;
  meanBars: number;
  meanChordsPerBar: number;
  majorKeys: RankedCount[];
  minorKeys: RankedCount[];
  minorShare: number;
  styles: RankedCount[];
  timeSigs: RankedCount[];
  /** Histogram of written form lengths (bars → tunes). */
  formLengths: RankedCount[];
  qualities: RankedCount[];
  families: RankedCount[];
  degrees: RankedCount[];
  rootMotion: RankedCount[];
  bigrams: RankedCount[];
  trigrams: RankedCount[];
  /** Chord duration in beats → occurrences (the corpus's harmonic rhythm). */
  durations: RankedCount[];
  iiVCount: number;
  iiVICount: number;
  /** Share of all transitions that are a ii→V step. */
  iiVShare: number;
  /** Share of tunes containing at least one full ii→V→I. */
  tunesWithIiVI: number;
  mostIiVDense: LeagueRow[];
  longestForms: LeagueRow[];
  densestHarmony: LeagueRow[];
  /** Mean bigram surprisal under the corpus model — "harmonic adventure". */
  mostAdventurous: LeagueRow[];
  leastAdventurous: LeagueRow[];
  facts: TuneFacts[];
}

const byCountDesc = (a: RankedCount, b: RankedCount) => b.count - a.count || a.label.localeCompare(b.label);

function ranked(map: Map<string, number>, limit = Infinity): RankedCount[] {
  return [...map.entries()]
    .map(([label, count]) => ({ label, count }))
    .sort(byCountDesc)
    .slice(0, limit);
}

const bump = (map: Map<string, number>, label: string, by = 1) =>
  map.set(label, (map.get(label) ?? 0) + by);

/** Circle-of-fifths presentation order for major keys (jazz's flat side first). */
const FIFTHS_ORDER = ['C', 'F', 'B♭', 'E♭', 'A♭', 'D♭', 'G♭', 'B', 'E', 'A', 'D', 'G', 'F♯', 'C♯', 'C♭'];

function keyOrdered(map: Map<string, number>): RankedCount[] {
  return [...map.entries()]
    .map(([label, count]) => ({ label, count }))
    .sort((a, b) => {
      const ai = FIFTHS_ORDER.indexOf(a.label.replace(' minor', ''));
      const bi = FIFTHS_ORDER.indexOf(b.label.replace(' minor', ''));
      return (ai < 0 ? 99 : ai) - (bi < 0 ? 99 : bi);
    });
}

/**
 * Each tune's harmonic adventure: the mean surprisal, in bits, of its
 * chord-to-chord moves under the corpus's own bigram model, where the corpus
 * is the tunes given. Diatonic cycling scores low; Coltrane changes score
 * high. NaN for a tune under nine chords, too short to rate fairly.
 */
export function harmonicSurprisal(facts: readonly TuneFacts[]): number[] {
  const bigrams = new Map<string, number>();
  let total = 0;
  for (const f of facts) {
    for (let i = 1; i < f.romans.length; i++) {
      bump(bigrams, `${f.romans[i - 1]} → ${f.romans[i]}`);
      total++;
    }
  }
  return facts.map((f) => {
    if (f.romans.length < 9) return NaN;
    let sum = 0;
    for (let i = 1; i < f.romans.length; i++) {
      const count = bigrams.get(`${f.romans[i - 1]} → ${f.romans[i]}`) ?? 1;
      sum += -Math.log2(count / total);
    }
    return sum / (f.romans.length - 1);
  });
}

export function analyzeCorpus(charts: IRealChart[]): CorpusStats {
  const facts = charts.map(tuneFacts);

  const majorKeys = new Map<string, number>();
  const minorKeys = new Map<string, number>();
  const styles = new Map<string, number>();
  const timeSigs = new Map<string, number>();
  const formLengths = new Map<string, number>();
  const qualities = new Map<string, number>();
  const families = new Map<string, number>();
  const degrees = new Map<string, number>();
  const rootMotion = new Map<string, number>();
  const bigrams = new Map<string, number>();
  const trigrams = new Map<string, number>();
  const durations = new Map<string, number>();
  const composers = new Set<string>();

  let totalChords = 0;
  let totalBars = 0;
  let transitions = 0;
  let iiVCount = 0;
  let iiVICount = 0;
  let tunesWithIiVI = 0;

  for (const f of facts) {
    if (f.composer) composers.add(f.composer);
    bump(f.key.minor ? minorKeys : majorKeys, f.key.label);
    if (f.style) bump(styles, f.style);
    bump(timeSigs, f.timeSig);
    bump(formLengths, String(f.bars));
    totalChords += f.chords;
    totalBars += f.bars;

    let hasIiVI = false;
    const seq = f.sequence;
    for (let i = 0; i < seq.length; i++) {
      bump(qualities, seq[i].chordType);
      bump(families, qualityFamily(seq[i].chordType));
      bump(degrees, `${degreeLabel(pitchClassOf(seq[i].root), f.key.tonicPc)}${qualityFamily(seq[i].chordType)}`);
      bump(durations, String(seq[i].durationBeats));
      if (i >= 1) {
        transitions++;
        const up = (((pitchClassOf(seq[i].root) - pitchClassOf(seq[i - 1].root)) % 12) + 12) % 12;
        bump(rootMotion, ROOT_MOTION_LABELS[up]);
        bump(bigrams, `${f.romans[i - 1]} → ${f.romans[i]}`);
        if (isIiV(seq[i - 1], seq[i])) iiVCount++;
      }
      if (i >= 2) {
        bump(trigrams, `${f.romans[i - 2]} → ${f.romans[i - 1]} → ${f.romans[i]}`);
        if (isIiVI(seq[i - 2], seq[i - 1], seq[i])) {
          iiVICount++;
          hasIiVI = true;
        }
      }
    }
    if (hasIiVI) tunesWithIiVI++;
  }

  const surprisals = harmonicSurprisal(facts);
  const rated = facts
    .map((f, i) => ({ title: f.title, composer: f.composer, value: surprisals[i] }))
    .filter((r) => Number.isFinite(r.value));
  const byValue = (dir: 1 | -1) => (a: LeagueRow, b: LeagueRow) => dir * (b.value - a.value);

  const league = (value: (f: TuneFacts) => number, top: number): LeagueRow[] =>
    facts
      .map((f) => ({ title: f.title, composer: f.composer, value: value(f) }))
      .sort(byValue(1))
      .slice(0, top);

  return {
    tunes: facts.length,
    composers: composers.size,
    totalChords,
    totalBars,
    meanBars: facts.length ? totalBars / facts.length : 0,
    meanChordsPerBar: facts.length
      ? facts.reduce((sum, f) => sum + f.chordsPerBar, 0) / facts.length
      : 0,
    majorKeys: keyOrdered(majorKeys),
    minorKeys: keyOrdered(minorKeys),
    minorShare: facts.length ? facts.filter((f) => f.key.minor).length / facts.length : 0,
    styles: ranked(styles, 12),
    timeSigs: ranked(timeSigs, 8),
    formLengths: ranked(formLengths, 10),
    qualities: ranked(qualities, 15),
    families: ranked(families),
    degrees: ranked(degrees, 15),
    rootMotion: ranked(rootMotion),
    bigrams: ranked(bigrams, 15),
    trigrams: ranked(trigrams, 15),
    durations: ranked(durations, 8),
    iiVCount,
    iiVICount,
    iiVShare: transitions > 0 ? iiVCount / transitions : 0,
    tunesWithIiVI,
    mostIiVDense: league((f) => f.iiVDensity, 8),
    longestForms: league((f) => f.bars, 8),
    densestHarmony: league((f) => f.chordsPerBar, 8),
    mostAdventurous: [...rated].sort(byValue(1)).slice(0, 8),
    leastAdventurous: [...rated].sort(byValue(-1)).slice(0, 8),
    facts,
  };
}
